/** Detalhes da tarefa em modal, com edição explícita por campo. */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  getClientes, getCrmMembers, pjGetTask, pjGetBoard, pjMoveTask, pjUpdateTask, pjDeleteTask, pjListProjects, pjTransferTask, pjCreateTask,
  pjListChecklist, pjCreateChecklistItem, pjUpdateChecklistItem, pjDeleteChecklistItem,
  pjListComments, pjCreateComment, pjListAttachments, pjDeleteAttachment,
  crmListActivityComments, crmCreateActivityComment,
  pjSignUpload, pjCreateAttachment, pjDownloadAttachment, pjUploadToSignedUrl,
  pjGetTaskHistory, pjAddAssignee, pjRemoveAssignee,
} from '../../db/api.js'
import { S as PS } from '../comercial/panelKit.jsx'
import ActivityDeadlinePicker from '../comercial/ActivityDeadlinePicker.jsx'
import TaskCompletionOverlay from './TaskCompletionOverlay.jsx'
import { buildTaskHistory } from '../../lib/taskHistory.js'
import { readCommercialTask, updateCommercialTask } from '../../lib/commercialTask.js'
import { newTaskDraft, taskCreationPayload } from '../../lib/newTaskDraft.js'
import '../comercial/dealPanel.css'
import { PRIO_ORDER, PRIO_LABEL, PRIO_BADGE, fmtDateTime, initials } from './projetosKit.jsx'
import { ACTIVITY_TYPE_LABELS, ACTIVITY_TASK_TYPES, ACTIVITY_TYPE_ICONS } from '../../constants/activityTypes.js'
import './projetos.css'

// Valor local compartilhado com o calendário de atividades do Comercial.
const toLocalInput = value => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const FIELD_LABEL = { contact_id: 'Cliente', project_id: 'Projeto', tipo: 'Tipo de tarefa', titulo: 'Nome da tarefa', descricao: 'Descrição', status: 'Status', prioridade: 'Prioridade', data_inicio: 'Início', prazo: 'Prazo', stage_id: 'Etapa', assignee_ids: 'Responsáveis' }

function trapFocus(e, root) {
  if (e.key !== 'Tab') return
  const els = [...root.querySelectorAll('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')].filter(el => el.getClientRects().length)
  const first = els[0], last = els[els.length - 1]
  if (e.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) { e.preventDefault(); last?.focus() }
  else if (!e.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) { e.preventDefault(); first?.focus() }
}

export default function TaskPanel({ taskId, source = 'project', creating = false, initialProjectId, onCreated, onClose, onChanged }) {
  const commercial = source === 'commercial'
  const readTask = commercial ? readCommercialTask : pjGetTask
  const updateTask = commercial ? updateCommercialTask : pjUpdateTask
  const { confirm } = useApp()
  const [task, setTask] = useState(null)
  const [board, setBoard] = useState(null)
  const [members, setMembers] = useState([])
  const [customers, setCustomers] = useState([])
  const [customerSearch, setCustomerSearch] = useState('')
  const [customersLoading, setCustomersLoading] = useState(false)
  const [projects, setProjects] = useState([])
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [tab, setTab] = useState('comentarios')
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [submittingNew, setSubmittingNew] = useState(false)
  const [editor, setEditor] = useState(null)
  const [draft, setDraft] = useState('')
  const [editError, setEditError] = useState(null)
  const [checklist, setChecklist] = useState([])
  const [historyKey, setHistoryKey] = useState(0)
  const [celebration, setCelebration] = useState(null)
  const [datePosition, setDatePosition] = useState({})
  const datePopupRef = useRef(null)
  const dialogRef = useRef(null), closeRef = useRef(null), editorRef = useRef(null)
  const savingRef = useRef(false)
  const cancelEditRef = useRef(false)

  useEffect(() => {
    if (!celebration) return
    if (celebration.taskId !== taskId || task?.status !== 'concluido') { setCelebration(null); return }
    const timeout = setTimeout(() => setCelebration(null), 2800)
    return () => clearTimeout(timeout)
  }, [celebration, taskId, task?.status])

  useEffect(() => {
    const previous = document.activeElement, overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    return () => { document.body.style.overflow = overflow; if (previous?.isConnected) previous.focus() }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true); setErr(null); setTask(null); setBoard(null); setEditor(null); setTab('comentarios')
    const load = async () => {
      try {
        if (creating) {
          const [projectOptions, people] = await Promise.all([pjListProjects(), getCrmMembers()])
          if (cancelled) return
          const available = projectOptions.filter(p => p.status !== 'arquivado')
          setProjects(available); setMembers(people)
          const initial = newTaskDraft()
          setTask(initial)
          if (initialProjectId && available.some(p => p.id === initialProjectId)) {
            const initialBoard = await pjGetBoard(initialProjectId)
            if (cancelled) return
            const first = initialBoard.columns?.find(c => c.id)
            setBoard(initialBoard)
            setTask({ ...initial, project_id: initialProjectId, stage_id: first?.id || null, status: first?.is_ganho ? 'concluido' : 'aberto' })
          }
          return
        }
        const t = await readTask(taskId)
        if (cancelled) return
        setTask(t)
        const results = await Promise.allSettled([commercial ? Promise.resolve({ project: { nome: t.pipeline_nome }, columns: [{ id: t.stage_id, nome: t.stage_nome }] }) : pjGetBoard(t.project_id), getCrmMembers(), commercial ? Promise.resolve([]) : pjListChecklist(taskId)])
        if (cancelled) return
        if (results[0].status === 'fulfilled') setBoard(results[0].value)
        if (results[1].status === 'fulfilled') setMembers(results[1].value)
        if (results[2].status === 'fulfilled') setChecklist(results[2].value)
        if (results.some(r => r.status === 'rejected')) setErr('Alguns detalhes não puderam ser carregados. Feche e abra a tarefa para tentar novamente.')
      } catch (e) { if (!cancelled) setErr(e.message) }
      finally { if (!cancelled) setLoading(false) }
    }
    load()
    return () => { cancelled = true }
  }, [taskId, commercial, creating, initialProjectId])

  useEffect(() => {
    if (creating && !loading) dialogRef.current?.querySelector('[aria-label="Nome da nova tarefa"]')?.focus()
  }, [creating, loading])

  useEffect(() => {
    if (editor) {
      const input = ['data_inicio', 'prazo'].includes(editor)
        ? datePopupRef.current?.querySelector('[aria-pressed="true"], .dp-deadline-days button')
        : editorRef.current?.querySelector('input, textarea, [role="menuitemradio"]')
      input?.focus()
      if (editor === 'titulo') input?.select()
    }
  }, [editor])

  useLayoutEffect(() => {
    if (!['data_inicio', 'prazo'].includes(editor)) return
    const popup = datePopupRef.current
    const place = () => {
      const anchor = editorRef.current?.querySelector('[data-task-field]')?.getBoundingClientRect()
      if (!anchor || !popup) return
      const width = Math.min(280, window.innerWidth - 16)
      const height = popup.offsetHeight
      setDatePosition({ width, left: Math.max(8, Math.min(anchor.left, window.innerWidth - width - 8)), top: Math.max(8, anchor.bottom + height + 8 <= window.innerHeight ? anchor.bottom + 6 : anchor.top - height - 6) })
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(popup)
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => { observer.disconnect(); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true) }
  }, [editor])

  const memberName = uid => {
    const m = members.find(x => x.id === uid)
    return m?.nome || m?.email || uid
  }
  useEffect(() => {
    if (editor !== 'project_id' || creating) return
    let cancelled = false
    pjListProjects().then(rows => {
      if (!cancelled) setProjects(rows.filter(p => p.status !== 'arquivado'))
    }).catch(e => { if (!cancelled) setEditError(e.message) })
      .finally(() => { if (!cancelled) setProjectsLoading(false) })
    return () => { cancelled = true }
  }, [editor, creating])
  useEffect(() => {
    if (editor !== 'contact_id' || commercial) return undefined
    let active = true
    setCustomersLoading(true); setCustomers([]); setEditError(null)
    const timer = setTimeout(() => {
      getClientes(customerSearch.trim() || undefined).then(value => { if (active) setCustomers(value || []) })
        .catch(e => { if (active) setEditError(e.message) })
        .finally(() => { if (active) setCustomersLoading(false) })
    }, 250)
    return () => { active = false; clearTimeout(timer) }
  }, [editor, customerSearch, commercial])
  const restoreFieldFocus = field => requestAnimationFrame(() => dialogRef.current?.querySelector(`[data-task-field="${field}"]`)?.focus())
  const closeEditor = () => {
    if (savingRef.current) return
    cancelEditRef.current = true
    setEditor(null); setEditError(null); restoreFieldFocus(editor)
  }
  useEffect(() => {
    if (!editor || ['titulo', 'descricao'].includes(editor)) return
    const outside = e => {
      if (!savingRef.current && !editorRef.current?.contains(e.target)) { setEditor(null); setEditError(null) }
    }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [editor])
  const openEditor = field => {
    if (savingRef.current) return
    cancelEditRef.current = false
    setEditError(null)
    if (field === 'contact_id') { setCustomerSearch(''); setCustomers([]); setCustomersLoading(true) }
    if (field === 'project_id' && !creating) { setProjects([]); setProjectsLoading(true) }
    const initial = field === 'assignee_ids' ? [...(task.assignee_ids || [])] : ['prazo','data_inicio'].includes(field) ? toLocalInput(task[field] || (field === 'prazo' || creating ? new Date() : task.created_at)) : task[field] || ''
    setDraft(initial)
    setEditor(field)
  }
  const saveField = async (nextDraft = draft, keepOpen = false) => {
    if (savingRef.current || cancelEditRef.current) return
    const field = editor
    if (field === 'titulo' && !nextDraft.trim()) { setEditError('Informe o nome da tarefa.'); return }
    const isDate = ['prazo', 'data_inicio'].includes(field)
    if (isDate && nextDraft && Number.isNaN(new Date(nextDraft).getTime())) { setEditError('Informe uma data válida.'); return }
    const unchanged = field === 'assignee_ids' ? false : isDate ? nextDraft === toLocalInput(task[field]) : (field === 'titulo' ? nextDraft.trim() : nextDraft) === (task[field] || '')
    if (unchanged) { setEditor(null); setEditError(null); return }
    savingRef.current = true; setBusy(true); setEditError(null)
    try {
      let updated
      if (creating) {
        const value = isDate ? (nextDraft ? new Date(nextDraft).toISOString() : null) : field === 'contact_id' ? nextDraft || null : nextDraft
        updated = { [field]: value }
        if (field === 'project_id') {
          const nextBoard = await pjGetBoard(nextDraft)
          const first = nextBoard.columns?.find(c => c.id)
          setBoard(nextBoard)
          updated.stage_id = first?.id || null
          updated.status = first?.is_ganho ? 'concluido' : 'aberto'
        } else if (field === 'stage_id') {
          updated.status = board?.columns?.find(c => c.id === value)?.is_ganho ? 'concluido' : 'aberto'
        }
      } else if (commercial) {
        const value = isDate ? (nextDraft ? new Date(nextDraft).toISOString() : null) : field === 'contact_id' ? nextDraft || null : field === 'titulo' ? nextDraft.trim() : nextDraft
        updated = await updateTask(taskId, { [field]: value })
      } else if (field === 'assignee_ids') {
        const previous = task.assignee_ids || []
        for (const uid of previous.filter(id => !nextDraft.includes(id))) await pjRemoveAssignee(taskId, uid)
        for (const uid of nextDraft.filter(id => !previous.includes(id))) await pjAddAssignee(taskId, uid)
        updated = await pjGetTask(taskId)
      } else if (field === 'stage_id') {
        updated = await pjMoveTask(taskId, { toStageId: nextDraft })
        setHistoryKey(k => k + 1)
      } else if (field === 'project_id') {
        updated = await pjTransferTask(taskId, nextDraft)
        setBoard({ project: projects.find(p => p.id === nextDraft), columns: [] })
        setHistoryKey(k => k + 1)
        try { setBoard(await pjGetBoard(nextDraft)) }
        catch { setErr('Projeto alterado. Feche e abra a tarefa para recarregar as etapas e subtarefas.') }
      } else {
        const value = isDate ? (nextDraft ? new Date(nextDraft).toISOString() : null) : field === 'contact_id' ? nextDraft || null : field === 'titulo' ? nextDraft.trim() : nextDraft
        updated = await updateTask(taskId, { [field]: value })
      }
      if (field === 'contact_id') updated.contact_nome = customers.find(c => c.id === nextDraft)?.nome || null
      setTask(t => ({ ...t, ...updated }))
      if (keepOpen) setDraft(updated.assignee_ids || nextDraft)
      else { setEditor(null); restoreFieldFocus(field) }
      if (!creating) onChanged?.()
    } catch (e2) {
      setEditError(e2.message)
      if (field === 'assignee_ids' && !creating) {
        try { const fresh = await readTask(taskId); setTask(fresh); setDraft(fresh.assignee_ids || []); onChanged?.() } catch { /* mantém o erro original */ }
      }
    } finally { savingRef.current = false; setBusy(false) }
  }
  const createTask = async () => {
    if (savingRef.current || !task) return
    setErr(null)
    let payload
    try { payload = taskCreationPayload(task) } catch (e) { setErr(e.message); return }
    savingRef.current = true; setBusy(true); setSubmittingNew(true)
    try {
      const created = await pjCreateTask(task.project_id, payload)
      onCreated?.(created)
    } catch (e) { setErr(e.message) }
    finally { savingRef.current = false; setBusy(false); setSubmittingNew(false) }
  }
  const completeTask = async () => {
    if (savingRef.current || !task) return
    const reopening = task.status === 'concluido'
    savingRef.current = true; setBusy(true); setErr(null)
    try {
      if (reopening) {
        const accepted = await confirm({ title: 'Reabrir tarefa', message: 'Deseja reabrir a tarefa?', confirmText: 'Reabrir tarefa' })
        if (!accepted) return
      }
      const updated = await updateTask(taskId, { status: reopening ? 'aberto' : 'concluido' })
      setTask(t => ({ ...t, ...updated }))
      if (!reopening && updated.status === 'concluido') setCelebration({ taskId })
      else setCelebration(null)
      setEditor(null); setEditError(null); onChanged?.()
    } catch (e) { setErr(e.message) }
    finally {
      savingRef.current = false; setBusy(false)
      requestAnimationFrame(() => dialogRef.current?.querySelector('.pj-task-complete')?.focus())
    }
  }
  const remove = async () => {
    const ok = await confirm({ title: 'Excluir tarefa', message: `Mover "${task.titulo}" para as tarefas excluídas?`, confirmText: 'Excluir tarefa', danger: true })
    if (!ok) return
    try { await pjDeleteTask(taskId); onChanged?.(); onClose() } catch (e) { setErr(e.message) }
  }
  const checklistChanged = async () => {
    onChanged?.()
    try { setChecklist(await pjListChecklist(taskId)) } catch (e) { setErr(e.message) }
  }
  const editButton = (field, content, className = '') => {
    const active = editor === field
    const textField = ['titulo', 'descricao'].includes(field)
    const dateField = ['prazo', 'data_inicio'].includes(field)
    const options = field === 'project_id' ? projects : field === 'tipo' ? ACTIVITY_TASK_TYPES.map(id => ({id, nome:ACTIVITY_TYPE_LABELS[id]})) : field === 'status' ? [{id:'aberto',nome:'Aberta'},{id:'concluido',nome:'Concluída'}] : field === 'prioridade' ? PRIO_ORDER.filter(p => !commercial || p !== 'urgente').map(p => ({id:p,nome:PRIO_LABEL[p]})) : field === 'stage_id' ? (board?.columns || []).filter(c => c.id) : []
    const textKeys = e => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeEditor() }
      else if (e.key === 'Enter' && (field !== 'descricao' || e.ctrlKey || e.metaKey)) { e.preventDefault(); saveField() }
    }
    const controls = active && textField && <>
      <button type="button" aria-label="Salvar edição" disabled={busy || (field === 'titulo' && !draft.trim())} onClick={() => saveField()}><Icon n="check" s={14}/></button>
      <button type="button" aria-label="Cancelar edição" disabled={busy} onClick={closeEditor}><Icon n="x" s={14}/></button>
    </>
    return <span className={`pj-task-inline-field${textField ? ' is-text' : ''}`} ref={active ? editorRef : null}>
      {active && textField ? <span className={`pj-task-inline-editor${field === 'descricao' ? ' is-description' : ''}`} onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget) && !cancelEditRef.current) {
          saveField()
        }
      }}>
        {field === 'descricao' ? <textarea className="pj-task-inline-textarea" aria-label="Descrição" value={draft} disabled={busy} rows={Math.max(3, draft.split('\n').length + 1)} onChange={e => setDraft(e.target.value)} onKeyDown={textKeys}/> : <input className="pj-task-inline-input" type="text" aria-label={FIELD_LABEL[field]} value={draft} disabled={busy} onChange={e => setDraft(e.target.value)} onKeyDown={textKeys}/>}
        <span className="pj-task-inline-actions">{busy ? <small>Salvando…</small> : controls}</span>
      </span> : <button type="button" data-task-field={field} disabled={busy} className={`pj-task-value ${className}`} onClick={() => active ? closeEditor() : openEditor(field)} aria-label={`Editar ${FIELD_LABEL[field].toLowerCase()}`} aria-expanded={!textField ? active : undefined} aria-haspopup={dateField ? 'dialog' : !textField ? 'menu' : undefined}>{dateField && <Icon n="calendar" s={13}/>} {content}</button>}
      {active && dateField && <div ref={datePopupRef} role="dialog" aria-label={`Alterar ${FIELD_LABEL[field].toLowerCase()}`} className="dp-activity-inline-editor dp-activity-popover" style={datePosition}>
        <strong className="dp-activity-popover-title">{field === 'prazo' ? 'Prazo da tarefa' : 'Início da tarefa'}</strong>
        <ActivityDeadlinePicker value={draft} onChange={setDraft} disabled={busy} clearLabel={field === 'prazo' ? 'Sem prazo' : 'Sem início'} timeLabel={field === 'prazo' ? 'Horário do prazo' : 'Horário do início'}/>
        <footer><button type="button" disabled={busy} onClick={closeEditor}>Cancelar</button><button type="button" disabled={busy} onClick={() => saveField()}>{busy ? 'Salvando…' : 'Salvar'}</button></footer>
        {editError && <p role="alert">{editError}</p>}
      </div>}
      {active && !textField && !dateField && <span className="pj-task-field-menu" role="menu" aria-label={FIELD_LABEL[field]} onKeyDown={e => {
        if (['ArrowDown','ArrowUp','Home','End'].includes(e.key)) {
          e.preventDefault(); e.stopPropagation()
          const items = [...e.currentTarget.querySelectorAll('button:not(:disabled), input:not(:disabled)')]
          const current = items.indexOf(document.activeElement)
          const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : (current + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
          items[next]?.focus()
        }
      }}>
        {field === 'contact_id' ? <>
          <input className="pj-task-customer-search" type="search" aria-label="Buscar cliente da tarefa" placeholder="Buscar por nome, e-mail ou empresa…" value={customerSearch} disabled={busy} onChange={e => { setCustomerSearch(e.target.value); setCustomers([]); setCustomersLoading(true) }}/>
          <button type="button" role="menuitemradio" aria-checked={!task.contact_id} disabled={busy} onClick={() => saveField('')}>Sem cliente{!task.contact_id && <Icon n="check" s={14}/>}</button>
          {customersLoading ? <span className="pj-task-menu-hint" role="status">Buscando clientes…</span> : customers.map(customer => <button type="button" role="menuitemradio" key={customer.id} aria-checked={task.contact_id === customer.id} disabled={busy} onClick={() => saveField(customer.id)}><span className="pj-task-customer-option"><span>{customer.nome || 'Sem nome'}</span><small>{[customer.empresa, customer.email].filter(Boolean).join(' · ')}</small></span>{task.contact_id === customer.id && <Icon n="check" s={14}/>}</button>)}
          {!customersLoading && !customers.length && !editError && <span className="pj-task-menu-hint">Nenhum cliente encontrado.</span>}
          <span className="pj-task-menu-hint">Vínculo desta tarefa, independente do cliente do projeto.</span>
        </> : field === 'assignee_ids' && commercial ? <>
          {[{id:'',nome:'Sem responsável'}, ...members].map(m => <button type="button" role="menuitemradio" key={m.id} aria-checked={(task.assignee_ids?.[0] || '') === m.id} disabled={busy} onClick={() => saveField(m.id ? [m.id] : [])}>{m.nome || m.email}{(task.assignee_ids?.[0] || '') === m.id && <Icon n="check" s={14}/>}</button>)}
        </> : field === 'assignee_ids' ? <>
          {members.length === 0 && <span className="pj-task-menu-hint">Nenhum membro disponível.</span>}
          {[...members, ...(task.assignee_ids || []).filter(id => !members.some(m => m.id === id)).map(id => ({id}))].map(m => <label key={m.id} className="pj-task-member-option"><input type="checkbox" disabled={busy} checked={draft.includes(m.id)} onChange={e => saveField(e.target.checked ? [...draft, m.id] : draft.filter(id => id !== m.id), true)}/><span className="pj-av pj-av-sm">{initials(memberName(m.id))}</span>{memberName(m.id)}</label>)}
        </> : options.map(option => <button key={option.id} type="button" role="menuitemradio" aria-checked={task[field] === option.id} disabled={busy} onClick={() => saveField(option.id)}><span>{field === 'tipo' && <Icon n={ACTIVITY_TYPE_ICONS[option.id] || 'page'} s={14}/>} {option.nome}</span>{task[field] === option.id && <Icon n="check" s={14}/>}</button>)}
        {busy && <span className="pj-task-menu-hint">{creating ? 'Carregando…' : 'Salvando…'}</span>}
        {field === 'project_id' && <span className="pj-task-menu-hint">{projectsLoading ? 'Carregando projetos…' : projects.length === 0 ? 'Nenhum projeto disponível.' : creating ? 'Selecione o projeto desta tarefa.' : `A etapa será ajustada ao novo projeto, mantendo o status. Subtarefas serão levadas junto.${task.parent_task_id ? ' Esta subtarefa será desvinculada da tarefa principal.' : ''}`}</span>}
        {field === 'stage_id' && <span className="pj-task-menu-hint">O status acompanha a etapa do Kanban.</span>}
      </span>}
      {active && editError && !dateField && <span className="pj-task-inline-error" role="alert">{editError}</span>}
    </span>
  }

  const stage = board?.columns?.find(c => c.id === task?.stage_id)
  const allTasks = creating ? [] : board?.columns?.flatMap(c => c.tasks || []) || []
  const parent = allTasks.find(t => t.id === task?.parent_task_id)
  const subtasks = allTasks.filter(t => t.parent_task_id === taskId)
  const doneCount = checklist.filter(i => i.done).length

  return createPortal(<div className="pj-task-overlay" onKeyDown={e => {
    if (celebration) { e.preventDefault(); e.stopPropagation(); if (e.key === 'Escape') setCelebration(null); return }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); if (editor) closeEditor(); else if (!busy) onClose(); return }
    trapFocus(e, dialogRef.current)
    e.stopPropagation()
  }}>
    <section className="pj-panel pj-task-modal" ref={dialogRef} role="dialog" aria-modal="true" aria-label={creating ? 'Nova tarefa' : 'Detalhes da tarefa'}>
      <header className="pj-panel-top">
        <div className="pj-task-breadcrumb"><Icon n={commercial ? 'columns' : 'folder'} s={16} /><span>{board?.project?.nome || (commercial ? 'Funil comercial' : 'Projeto')}</span><span className="pj-task-separator">/</span><Icon n="page" s={15}/><strong>{stage?.nome || 'Tarefa'}</strong></div>
        <div className="pj-task-header-actions">{creating ? <span>Nova tarefa · Ainda não salva</span> : task && <span>Criada em {fmtDateTime(task.created_at)}</span>}<button ref={closeRef} className="pj-panel-ico" onClick={onClose} disabled={busy} aria-label={creating ? 'Fechar nova tarefa' : 'Fechar detalhes da tarefa'}><Icon n="x" s={19} /></button></div>
      </header>
      {err && <div role="alert" style={{ ...PS.error, margin: '8px 24px' }}>{err}</div>}
      {loading ? <div style={PS.muted}>Carregando…</div> : task && <div className="pj-task-workspace">
        <div className="pj-task-main-scroll"><main className="pj-task-main">
          <div className="pj-task-heading-row">
            <div className="pj-task-kind"><span className="pj-task-type"><Icon n={ACTIVITY_TYPE_ICONS[task.tipo] || 'page'} s={13}/>{ACTIVITY_TYPE_LABELS[task.tipo] || 'Tarefa'}</span>{task.parent_task_id && <span className="pj-task-subtype">Subtarefa</span>}</div>
            {creating ? <button type="button" className="pj-task-complete" onClick={createTask} disabled={busy || !task.titulo.trim() || !task.project_id || ['data_inicio', 'prazo'].includes(editor)}><Icon n="plus" s={15}/>{submittingNew ? 'Criando…' : 'Criar tarefa'}</button> : <button type="button" className={`pj-task-complete${task.status === 'concluido' ? ' is-completed' : ''}`} onClick={completeTask} disabled={busy || !!celebration || ['titulo', 'descricao', 'data_inicio', 'prazo'].includes(editor)} title={['titulo', 'descricao', 'data_inicio', 'prazo'].includes(editor) ? 'Finalize a edição do campo para alterar o status da tarefa' : task.status === 'concluido' ? 'Reabrir tarefa' : undefined}>
              <Icon n="check" s={15} />{task.status === 'concluido' ? 'Concluída' : 'Concluir tarefa'}
            </button>}
          </div>
          {parent && <div className="pj-task-context">Tarefa principal: {parent.titulo}</div>}
          {commercial && <div className="pj-task-context">Comercial · {task.deal_nome}</div>}
          <h2 className="pj-task-title">{creating ? <input className="pj-task-inline-input" aria-label="Nome da nova tarefa" placeholder="Nome da tarefa" value={task.titulo} disabled={busy} onChange={e => setTask(t => ({ ...t, titulo: e.target.value }))}/> : editButton('titulo', task.titulo)}</h2>
          <div className="pj-task-properties">
            <div><span className="pj-panel-label"><Icon n="status" s={15}/>Status</span>{creating ? <span className="pj-task-status" title="O status inicial acompanha a etapa selecionada.">{task.status === 'concluido' ? 'Concluída' : 'Aberta'}</span> : editButton('status', <><Icon n={task.status === 'concluido' ? 'check' : 'clock'} s={14} />{task.status === 'concluido' ? 'Concluída' : 'Aberta'}<Icon n="chevron_d" s={12}/></>, `pj-task-status${task.status === 'concluido' ? ' is-done' : ''}`)}</div>
            <div><span className="pj-panel-label"><Icon n="users" s={15}/>Responsáveis</span>{editButton('assignee_ids', task.assignee_ids?.length ? <span className="pj-task-people">{task.assignee_ids.map(uid => <span key={uid}><span className="pj-av pj-av-sm">{initials(memberName(uid))}</span>{memberName(uid)}</span>)}</span> : 'Sem responsável')}</div>
            <div><span className="pj-panel-label"><Icon n="calendar" s={15}/>Datas</span><div className="pj-task-dates">{editButton('data_inicio', task.data_inicio ? fmtDateTime(task.data_inicio) : 'Início')}<span>→</span>{editButton('prazo', task.prazo ? fmtDateTime(task.prazo) : 'Prazo')}</div></div>
            <div><span className="pj-panel-label"><Icon n="arrow_up" s={15}/>Prioridade</span>{editButton('prioridade', <><span className="pj-dot" style={{ background: (PRIO_BADGE[task.prioridade] || PRIO_BADGE.media).fg }} />{PRIO_LABEL[task.prioridade] || 'Média'}</>)}</div>
            <div><span className="pj-panel-label"><Icon n="columns" s={15}/>{commercial ? 'Etapa do negócio' : 'Etapa'}</span>{commercial ? <span>{task.stage_nome}</span> : board?.columns?.some(c => c.id) ? editButton('stage_id', <>{stage?.nome || 'Sem etapa'}<Icon n="chevron_d" s={12}/></>) : <span>{creating && !task.project_id ? 'Selecione um projeto' : 'Sem etapa disponível'}</span>}</div>
            <div><span className="pj-panel-label"><Icon n={commercial ? 'page' : 'check'} s={15}/>{commercial ? 'Negócio' : 'Checklist'}</span><span>{commercial ? task.deal_nome : `${doneCount} de ${checklist.length} concluídos`}</span></div>
            <div><span className="pj-panel-label"><Icon n="folder" s={15}/>{commercial ? 'Funil comercial' : 'Projeto'}</span>{commercial ? <span>{task.pipeline_nome}</span> : editButton('project_id', <>{board?.project?.nome || 'Selecionar projeto'}<Icon n="chevron_d" s={12}/></>)}</div>
            <div><span className="pj-panel-label"><Icon n="page" s={15}/>Tipo</span>{editButton('tipo', <><Icon n={ACTIVITY_TYPE_ICONS[task.tipo] || 'page'} s={14}/>{ACTIVITY_TYPE_LABELS[task.tipo] || 'Tarefa'}<Icon n="chevron_d" s={12}/></>)}</div>
            {!commercial && <div><span className="pj-panel-label"><Icon n="user" s={15}/>Cliente</span>{editButton('contact_id', <>{task.contact_nome || (task.contact_id ? 'Cliente indisponível' : 'Vincular cliente')}<Icon n="chevron_d" s={12}/></>)}</div>}
          </div>
          <section className="pj-task-description-section" aria-label="Descrição da tarefa">{creating ? <textarea className="pj-task-inline-textarea pj-task-new-description" aria-label="Descrição da nova tarefa" placeholder="Adicione uma descrição…" value={task.descricao} disabled={busy} rows={4} onChange={e => setTask(t => ({ ...t, descricao: e.target.value }))}/> : editButton('descricao', task.descricao || 'Adicione uma descrição…', `pj-task-description${!task.descricao ? ' is-empty' : ''}`)}</section>
          {subtasks.length > 0 && <details className="pj-task-section" open><summary><Icon n="page" s={15}/>Subtarefas <span>{subtasks.length}</span></summary><div className="pj-task-subtasks">{subtasks.map(t => <div key={t.id}><Icon n={t.status === 'concluido' ? 'check' : 'page'} s={15}/><span>{t.titulo}</span><small>{t.status === 'concluido' ? 'Concluída' : 'Aberta'}</small></div>)}</div></details>}
          {!commercial && !creating && <><details className="pj-task-section" open={checklist.length > 0 || undefined}><summary><Icon n="check" s={15}/>Checklist <span>{doneCount}/{checklist.length}</span></summary><div className="pj-task-section-body"><ChecklistTab taskId={taskId} onChanged={checklistChanged} /></div></details>
          <details className="pj-task-section"><summary><Icon n="clip" s={15}/>Anexos</summary><div className="pj-task-section-body"><AttachmentsTab taskId={taskId} /></div></details></>}
          {creating ? <p className="pj-task-history-note">Nome e projeto são obrigatórios. Os demais campos são opcionais.</p> : <div className="pj-task-metadata">{task.updated_at && <span>Atualizada em {fmtDateTime(task.updated_at)}</span>}{task.concluido_at && <span>Concluída em {fmtDateTime(task.concluido_at)}</span>}{!commercial && <button className="pj-task-value pj-task-delete" disabled={busy} onClick={remove}><Icon n="trash" s={13}/>Excluir tarefa</button>}</div>}
        </main></div>
        {creating ? <aside className="pj-task-activity" aria-label="Sobre a nova tarefa"><div className="pj-task-activity-head"><strong>Nova tarefa</strong></div><div className="pj-task-activity-content"><p className="pj-task-history-note">Preencha os detalhes e clique em Criar tarefa para salvar.</p><p className="pj-task-history-note">Depois de criar, você poderá adicionar checklist, anexos e comentários nesta mesma tela.</p><button type="button" className="pj-task-value" onClick={onClose} disabled={busy}>Cancelar criação</button></div></aside> : <aside className="pj-task-activity" aria-label="Atividade da tarefa">
          <div className="pj-task-activity-head"><strong>Atividade</strong><div className="pj-panel-tabs" role="tablist" aria-label="Atividade da tarefa">{['comentarios','historico'].map(t => <button key={t} role="tab" aria-selected={tab === t} className={`pj-panel-tab${tab === t ? ' is-active' : ''}`} onClick={() => setTab(t)}>{{comentarios:'Comentários',historico:'Histórico'}[t]}</button>)}</div></div>
          <div className={`pj-task-activity-content${tab === 'comentarios' ? ' is-comments' : ''}`} role="tabpanel">
            {tab === 'comentarios' && <CommentsTab taskId={taskId} memberName={memberName} commercial={commercial} />}
            {tab === 'historico' && <HistoryTab key={historyKey} task={task} members={members} commercial={commercial} />}
          </div>
          <div className="pj-task-created"><span className="pj-av pj-av-sm">{initials(task.created_by ? memberName(task.created_by) : 'Sistema')}</span><span>{task.created_by ? memberName(task.created_by) : 'Sistema'} criou esta tarefa<small>{fmtDateTime(task.created_at)}</small></span></div>
        </aside>}
      </div>}

      {celebration?.taskId === taskId && task?.status === 'concluido' && <TaskCompletionOverlay icon={ACTIVITY_TYPE_ICONS[task.tipo] || 'page'} onEnd={() => setCelebration(null)}/>}
    </section>

  </div>, document.body)
}

// ── Checklist ───────────────────────────────────────────────────────────────
function ChecklistTab({ taskId, onChanged }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [novo, setNovo] = useState('')

  const load = async () => { setItems(await pjListChecklist(taskId)); setLoading(false) }
  useEffect(() => { setLoading(true); load() }, [taskId])

  const add = async () => {
    if (!novo.trim()) return
    await pjCreateChecklistItem(taskId, novo.trim()); setNovo(''); load(); onChanged?.()
  }
  const toggle = async (it) => { await pjUpdateChecklistItem(it.id, { done: !it.done }); load(); onChanged?.() }
  const del = async (it) => { await pjDeleteChecklistItem(it.id); load(); onChanged?.() }

  const doneCount = items.filter((i) => i.done).length

  if (loading) return <div style={PS.muted}>Carregando…</div>
  return (
    <div>
      {items.length > 0 && (
        <div className="pj-checkprog">
          <div className="pj-checkprog-label">{doneCount} de {items.length} concluídos</div>
          <div className="pj-bar">
            <div className="pj-bar-fill" style={{ width: `${items.length ? doneCount / items.length * 100 : 0}%` }} />
          </div>
        </div>
      )}
      {items.map((it) => (
        <div key={it.id} className="pj-check-row">
          <label>
            <input type="checkbox" className="pj-cb" checked={it.done} onChange={() => toggle(it)} />
            <span className={`pj-check-text${it.done ? ' is-done' : ''}`}>{it.texto}</span>
          </label>
          <ChecklistItemMenu onRemove={() => del(it)} />
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <input className="pj-field" placeholder="Novo item…" value={novo}
          onChange={(e) => setNovo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add() }} />
        <button className="pj-btn-sm" onClick={add}><Icon n="plus" s={15} sw={2} /></button>
      </div>
    </div>
  )
}

// ── Menu de item do checklist ("..." → Remover) ──────────────────────────────
function ChecklistItemMenu({ onRemove }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button className="pj-row-more" onClick={() => setOpen((o) => !o)} title="Mais ações">
        <Icon n="more" s={16} />
      </button>
      {open && (
        <div className="pj-menu" style={{ top: '100%', right: 0, marginTop: 4, minWidth: 150 }} role="menu">
          <button className="pj-menu-item is-danger" onClick={() => { setOpen(false); onRemove() }}>
            <Icon n="trash" s={14} /> Remover
          </button>
        </div>
      )}
    </div>
  )
}

// ── Comentários ─────────────────────────────────────────────────────────────
function CommentsTab({ taskId, memberName, commercial = false }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [novo, setNovo] = useState('')
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [retry, setRetry] = useState(0)
  const listComments = commercial ? crmListActivityComments : pjListComments
  const createComment = commercial ? crmCreateActivityComment : pjCreateComment

  useEffect(() => {
    let active = true
    setLoading(true); setError(null)
    listComments(taskId).then(rows => { if (active) setItems(rows) })
      .catch(e => { if (active) setError(e.message || 'Não foi possível carregar os comentários.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [taskId, commercial, retry])

  const add = async () => {
    if (!novo.trim() || saving) return
    setSaving(true); setError(null)
    try {
      const comment = await createComment(taskId, novo.trim())
      setItems(current => [...current, comment]); setNovo('')
    } catch (e) { setError(e.message || 'Não foi possível enviar o comentário.') }
    finally { setSaving(false) }
  }

  if (loading) return <div style={PS.muted}>Carregando…</div>
  return (
    <div className="pj-task-comments">
      <div className="pj-task-comments-feed">
      {error && <div role="alert" className="pj-task-history-error">{error} <button type="button" onClick={() => setRetry(n => n + 1)}>Tentar novamente</button></div>}
      {!error && !items.length && <div style={PS.empty}>Nenhum comentário ainda.</div>}
      {items.map((c) => (
        <div key={c.id} className="pj-comment">
          <div className="pj-comment-head">
            <span className="pj-av pj-av-sm">{initials(memberName(c.autor_id))}</span>
            <span className="pj-comment-author">{c.autor_id ? memberName(c.autor_id) : 'Sistema'}</span>
            <span className="pj-comment-date">{fmtDateTime(c.created_at)}</span>
          </div>
          <div className="pj-comment-body">{c.corpo}</div>
        </div>
      ))}
      </div>
      <div className="pj-task-comment-compose">
        <textarea className="pj-area" aria-label="Escreva um comentário" placeholder="Escreva um comentário…" value={novo} maxLength={commercial ? 10000 : undefined} disabled={saving}
          onChange={(e) => setNovo(e.target.value)} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="pj-btn-sm" onClick={add} disabled={!novo.trim() || saving}>{saving ? 'Enviando…' : 'Comentar'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Anexos (Supabase Storage — upload mediado pelo backend via signed URL) ────
function AttachmentsTab({ taskId }) {
  const { confirm } = useApp()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState(null)

  const load = async () => { setItems(await pjListAttachments(taskId)); setLoading(false) }
  useEffect(() => { setLoading(true); load() }, [taskId])

  const onPick = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''   // permite re-selecionar o mesmo arquivo
    if (!files.length) return
    setUploading(true); setErr(null)
    try {
      for (const file of files) {
        const { upload_url, storage_path } = await pjSignUpload(taskId, file.name)
        await pjUploadToSignedUrl(upload_url, file)
        await pjCreateAttachment(taskId, {
          nome: file.name, storage_path,
          mime: file.type || null, tamanho: file.size || null,
        })
      }
      await load()
    } catch (e2) { setErr(e2.message) } finally { setUploading(false) }
  }

  const download = async (a) => {
    try {
      const { url } = await pjDownloadAttachment(a.id)
      window.open(url, '_blank', 'noopener')
    } catch (e2) { setErr(e2.message) }
  }

  const del = async (a) => {
    const ok = await confirm({
      title: 'Remover anexo',
      message: `Remover o anexo "${a.nome}"?`,
      confirmText: 'Remover', danger: true,
    })
    if (!ok) return
    try { await pjDeleteAttachment(a.id); load() } catch (e2) { setErr(e2.message) }
  }

  if (loading) return <div style={PS.muted}>Carregando…</div>
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <label className="pj-btn-sm" style={{ cursor: uploading ? 'wait' : 'pointer' }}>
          <Icon n="upload" s={14} /> {uploading ? 'Enviando…' : 'Enviar arquivo'}
          <input type="file" multiple hidden onChange={onPick} disabled={uploading} />
        </label>
      </div>
      {err && <div style={PS.error}>{err}</div>}
      {!items.length ? (
        <div style={PS.empty}>Nenhum anexo.</div>
      ) : (
        items.map((a) => (
          <div key={a.id} className="pj-check-row">
            <button className="pj-file" onClick={() => download(a)} title="Baixar">
              <Icon n="page" s={15} />
              <div style={{ minWidth: 0 }}>
                <div className="pj-check-text">{a.nome}</div>
                <div style={{ fontSize: 12, color: '#667085' }}>
                  {a.mime || '—'}{a.tamanho ? ` · ${Math.round(a.tamanho / 1024)} KB` : ''}
                </div>
              </div>
            </button>
            <button className="pj-row-more" onClick={() => del(a)} title="Remover"><Icon n="x" s={14} sw={2} /></button>
          </div>
        ))
      )}
    </div>
  )
}

// ── Histórico disponível, sem inferir alterações não registradas ─────────────
function HistoryTab({ task, members, commercial = false }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    if (commercial) { setItems([]); setLoading(false); return }
    let cancelled = false
    setLoading(true); setError(false); setItems([])
    pjGetTaskHistory(task.id).then(rows => { if (!cancelled) setItems(rows) })
      .catch(() => { if (!cancelled) setError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [task.id, retry, commercial])

  const groups = new Map()
  for (const event of buildTaskHistory(task, items)) {
    const day = new Date(event.at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
    if (!groups.has(day)) groups.set(day, [])
    groups.get(day).push(event)
  }
  return <div className="pj-task-history">
    <p className="pj-task-history-note">{commercial ? 'Criação registrada. O Comercial não registra a data nem o autor da conclusão.' : 'Criação, movimentações registradas e conclusão atual.'}</p>
    {loading && <p style={PS.muted}>Carregando movimentações…</p>}
    {error && <div className="pj-task-history-error" role="alert">Não foi possível carregar as movimentações. <button type="button" onClick={() => setRetry(n => n + 1)}>Tentar novamente</button></div>}
    {!groups.size && !loading && !error && <div style={PS.empty}>Nenhum registro disponível.</div>}
    {[...groups].map(([day, events]) => <section key={day} aria-label={day}>
      <h4>{day}</h4>
      <ol>{events.map(event => {
        const member = event.actorId ? members.find(m => m.id === event.actorId) : null
        const actor = member?.nome || member?.email || (event.actorId ? 'Usuário não identificado' : 'Autor não registrado')
        return <li key={event.id}>
          <span className={`pj-task-history-icon is-${event.kind}`} aria-hidden="true"><Icon n={event.kind === 'completed' ? 'check' : event.kind === 'created' ? 'plus' : 'columns'} s={14}/></span>
          <div className="pj-task-history-entry"><div className="pj-task-history-heading"><strong>{event.title}</strong><time dateTime={event.at} title={fmtDateTime(event.at)}>{new Date(event.at).toLocaleTimeString('pt-BR', {hour:'2-digit', minute:'2-digit'})}</time></div>
            {event.detail && <p>{event.detail}</p>}
            <span className="pj-task-history-actor">{member && <span className="pj-av pj-av-sm" aria-hidden="true">{initials(actor)}</span>}{actor}</span>
          </div>
        </li>
      })}</ol>
    </section>)}
  </div>
}
